import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar, Car, MapPin, Phone, Mail, User, Clock, FileText, X, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Invoice } from './Invoice';

// Use the correct backend URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const BookingsAdmin = ({ hideHeader = false }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [acceptingBooking, setAcceptingBooking] = useState(null);
  const [carDriverDetails, setCarDriverDetails] = useState({
    carModel: '',
    carRegistration: '',
    driverName: '',
    driverPhone: ''
  });

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings`);
      setBookings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
      setLoading(false);
    }
  };

  const getServiceTypeBadge = (type) => {
    const colors = {
      'airport': 'bg-blue-100 text-blue-700',
      'outstation': 'bg-green-100 text-green-700',
      'rental': 'bg-purple-100 text-purple-700',
      'corporate': 'bg-orange-100 text-orange-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const getVehicleBadge = (type) => {
    const colors = {
      'sedan': 'bg-indigo-100 text-indigo-700',
      'suv': 'bg-teal-100 text-teal-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Generate invoice number based on date: SGCAB-YYYYMMDD-XXX
  const generateInvoiceNumber = (booking, index) => {
    const date = new Date(booking.created_at || booking.date);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const sequence = String(index + 1).padStart(3, '0');
    return `SGCAB-${year}${month}${day}-${sequence}`;
  };

  const handleGenerateInvoice = (booking, index) => {
    setSelectedBooking({ ...booking, invoiceNumber: generateInvoiceNumber(booking, index) });
    setShowInvoice(true);
  };

  const handleCloseInvoice = () => {
    setShowInvoice(false);
    setSelectedBooking(null);
  };

  const handleDeclineBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to decline this booking?')) {
      return;
    }

    try {
      await axios.patch(`${BACKEND_URL}/api/bookings/${bookingId}/status`, {
        status: 'declined'
      });
      
      toast.success('Booking declined successfully');
      fetchBookings(); // Refresh the list
    } catch (error) {
      console.error('Error declining booking:', error);
      toast.error('Failed to decline booking');
    }
  };

  const handleAcceptBooking = (booking) => {
    setAcceptingBooking(booking);
    setShowAcceptModal(true);
    setCarDriverDetails({
      carModel: '',
      carRegistration: '',
      driverName: '',
      driverPhone: ''
    });
  };

  const handleConfirmAccept = async () => {
    // Validate required fields
    if (!carDriverDetails.carModel || !carDriverDetails.carRegistration || 
        !carDriverDetails.driverName || !carDriverDetails.driverPhone) {
      toast.error('Please fill in all car and driver details');
      return;
    }

    try {
      await axios.patch(`${BACKEND_URL}/api/bookings/${acceptingBooking.id}/accept`, {
        car_details: {
          model: carDriverDetails.carModel,
          registration: carDriverDetails.carRegistration
        },
        driver_details: {
          name: carDriverDetails.driverName,
          phone: carDriverDetails.driverPhone
        }
      });
      
      toast.success('Booking accepted successfully with car and driver details');
      setShowAcceptModal(false);
      setAcceptingBooking(null);
      fetchBookings(); // Refresh the list
    } catch (error) {
      console.error('Error accepting booking:', error);
      toast.error('Failed to accept booking');
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this confirmed booking?')) {
      return;
    }

    try {
      await axios.patch(`${BACKEND_URL}/api/bookings/${bookingId}/status`, {
        status: 'cancelled'
      });
      
      toast.success('Booking cancelled successfully');
      fetchBookings(); // Refresh the list
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Failed to cancel booking');
    }
  };

  // Helper function to check if booking time has passed
  const isBookingPast = (booking) => {
    if (!booking.date || !booking.time) return false; // Keep bookings without date/time
    
    try {
      const bookingDateTime = new Date(`${booking.date}T${booking.time}`);
      const now = new Date();
      return bookingDateTime < now;
    } catch (error) {
      return false; // Keep booking if date parsing fails
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={hideHeader ? "py-8" : "min-h-screen bg-gray-50 py-20"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        {!hideHeader && (
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Booking Requests</h1>
            <p className="text-gray-600">All customer booking requests in one place</p>
            <div className="mt-4 flex items-center space-x-4">
              <Badge className="bg-orange-600 text-white px-4 py-2">
                Total: {bookings.filter(b => !['declined', 'cancelled'].includes(b.status) && !isBookingPast(b)).length} active bookings
              </Badge>
            </div>
          </div>
        )}

        {/* Bookings List */}
        {bookings.filter(b => !['declined', 'cancelled'].includes(b.status) && !isBookingPast(b)).length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No active bookings</h3>
              <p className="text-gray-600">Upcoming booking requests will appear here once customers submit them.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {bookings
              .filter(booking => !['declined', 'cancelled'].includes(booking.status) && !isBookingPast(booking))
              .map((booking, index) => (
              <Card key={booking.id || index} className="border-2 border-gray-200 hover:border-orange-300 transition-all">
                <CardHeader className="bg-gradient-to-r from-orange-50 to-white border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl font-bold text-gray-900">
                      Booking #{bookings.length - index}
                    </CardTitle>
                    <div className="flex space-x-2">
                      {booking.status === 'confirmed' && (
                        <Badge className="bg-green-100 text-green-700 border border-green-300">
                          ✓ CONFIRMED
                        </Badge>
                      )}
                      <Badge className={getServiceTypeBadge(booking.serviceType)}>
                        {booking.serviceType?.toUpperCase() || 'N/A'}
                      </Badge>
                      <Badge className={getVehicleBadge(booking.vehicleType)}>
                        {booking.vehicleType?.toUpperCase() || 'N/A'}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Submitted: {formatDate(booking.created_at)}
                  </p>
                </CardHeader>
                
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Customer Info */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-gray-900 text-lg border-b pb-2">Customer Information</h3>
                      
                      <div className="flex items-center space-x-3">
                        <User className="w-5 h-5 text-orange-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-gray-500">Name</p>
                          <p className="font-semibold text-gray-900">{booking.name}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <Phone className="w-5 h-5 text-orange-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-gray-500">Phone</p>
                          <a href={`tel:${booking.phone}`} className="font-semibold text-orange-600 hover:underline">
                            {booking.phone}
                          </a>
                        </div>
                      </div>

                      {booking.email && (
                        <div className="flex items-center space-x-3">
                          <Mail className="w-5 h-5 text-orange-600 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-gray-500">Email</p>
                            <a href={`mailto:${booking.email}`} className="font-semibold text-orange-600 hover:underline">
                              {booking.email}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Trip Details */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-gray-900 text-lg border-b pb-2">Trip Details</h3>
                      
                      {booking.pickupLocation && (
                        <div className="flex items-start space-x-3">
                          <MapPin className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-500">Pickup</p>
                            <p className="font-semibold text-gray-900">{booking.pickupLocation}</p>
                          </div>
                        </div>
                      )}

                      {booking.dropLocation && (
                        <div className="flex items-start space-x-3">
                          <MapPin className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-500">Drop</p>
                            <p className="font-semibold text-gray-900">{booking.dropLocation}</p>
                          </div>
                        </div>
                      )}

                      {(booking.date || booking.time) && (
                        <div className="flex items-start space-x-3">
                          <Calendar className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-500">Schedule</p>
                            <p className="font-semibold text-gray-900">
                              {booking.date && formatDate(booking.date)}
                              {booking.date && booking.time && ' at '}
                              {booking.time}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  {booking.notes && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h3 className="font-semibold text-gray-900 mb-2">Additional Notes</h3>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <p className="text-gray-700 whitespace-pre-wrap">{booking.notes}</p>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-6 pt-6 border-t border-gray-200 flex flex-wrap gap-3">
                    {/* Show Accept/Decline for pending bookings, Cancel for confirmed bookings */}
                    {booking.status === 'confirmed' ? (
                      // Confirmed booking - show Cancel button
                      <button
                        onClick={() => handleCancelBooking(booking.id)}
                        className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold transition-colors"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel Booking
                      </button>
                    ) : (
                      // Pending booking - show Accept and Decline buttons
                      <>
                        <button
                          onClick={() => handleAcceptBooking(booking)}
                          className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Accept Booking
                        </button>
                        <button
                          onClick={() => handleDeclineBooking(booking.id)}
                          className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Decline
                        </button>
                      </>
                    )}
                    
                    {/* Common action buttons */}
                    <a 
                      href={`tel:${booking.phone}`}
                      className="inline-flex items-center px-4 py-2 bg-black hover:bg-gray-900 text-yellow-400 rounded-lg font-semibold transition-colors"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Call Customer
                    </a>
                    <a 
                      href={`https://wa.me/91${booking.phone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </a>
                    <button
                      onClick={() => handleGenerateInvoice(booking, index)}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Invoice
                    </button>
                    {booking.email && (
                      <a 
                        href={`mailto:${booking.email}`}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Invoice Modal */}
      {showInvoice && selectedBooking && (
        <Invoice 
          booking={selectedBooking} 
          invoiceNumber={selectedBooking.invoiceNumber}
          onClose={handleCloseInvoice} 
        />
      )}

      {/* Accept Booking Modal - Car & Driver Details */}
      {showAcceptModal && acceptingBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Accept Booking</h2>
              <button
                onClick={() => setShowAcceptModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <p className="text-gray-600 mb-6">
              Please provide car and driver details for <strong>{acceptingBooking.name}</strong>'s booking.
            </p>

            {/* Car Details */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Car className="w-5 h-5 mr-2 text-blue-600" />
                Car Details
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Car Model *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Toyota Innova Crysta"
                    value={carDriverDetails.carModel}
                    onChange={(e) => setCarDriverDetails({...carDriverDetails, carModel: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Registration Number *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., KA-01-AB-1234"
                    value={carDriverDetails.carRegistration}
                    onChange={(e) => setCarDriverDetails({...carDriverDetails, carRegistration: e.target.value.toUpperCase()})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                  />
                </div>
              </div>
            </div>

            {/* Driver Details */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <User className="w-5 h-5 mr-2 text-green-600" />
                Driver Details
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Driver Name *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Rajesh Kumar"
                    value={carDriverDetails.driverName}
                    onChange={(e) => setCarDriverDetails({...carDriverDetails, driverName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Driver Phone *
                  </label>
                  <input
                    type="tel"
                    placeholder="e.g., 9876543210"
                    value={carDriverDetails.driverPhone}
                    onChange={(e) => setCarDriverDetails({...carDriverDetails, driverPhone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowAcceptModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAccept}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
              >
                Confirm & Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
